<script setup>
/* Leiste unter dem Kopf des Profils: Pfeil zurück, "7 / 18", Pfeil weiter. Erscheint nur, wenn das Profil
   aus einer Liste geöffnet wurde (Kontext, siehe usePlayers.js profileContext). Die Wisch- und Tastaturlogik
   steht in composables/useProfileNav.js; hier nur die Anzeige. */
import { computed } from 'vue';
import { mdiChevronLeft, mdiChevronRight } from '@mdi/js';

const props = defineProps({
  position: { type: Object, default: null },
  canPrev: { type: Boolean, default: false },
  canNext: { type: Boolean, default: false },
  skipped: { type: Number, default: 0 },
  disabled: { type: Boolean, default: false },
});
defineEmits(['step']);

/* Fortschrittslinie wie unter der Seitenleiste der Startseite: Anteil = Platz / Anzahl */
const progress = computed(() =>
  props.position ? `${(props.position.index / Math.max(props.position.total, 1)) * 100}%` : '0%'
);
</script>

<template>
  <div v-if="position" class="profile-nav">
    <button
      type="button"
      class="pn-btn"
      :disabled="!canPrev || disabled"
      :aria-label="$t('profileNav.previous')"
      :title="$t('profileNav.previous')"
      @click="$emit('step', -1)"
    >
      <v-icon :icon="mdiChevronLeft" size="16" />
    </button>
    <div class="pn-mid">
      <span class="pn-count" aria-live="polite">{{ position.index }} / {{ position.total }}</span>
      <span v-if="skipped" class="pn-note" role="status">{{ $t('profileNav.skipped', { n: skipped }, skipped) }}</span>
    </div>
    <button
      type="button"
      class="pn-btn"
      :disabled="!canNext || disabled"
      :aria-label="$t('profileNav.next')"
      :title="$t('profileNav.next')"
      @click="$emit('step', 1)"
    >
      <v-icon :icon="mdiChevronRight" size="16" />
    </button>
    <span class="pn-progress" aria-hidden="true"><span class="pn-progress-fill" :style="{ width: progress }"></span></span>
  </div>
</template>

<style scoped>
.profile-nav {
  position: relative;
  z-index: 3;
  flex: none;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0 8px;
  /* Seitenrand = gemeinsame Linie der Profilansicht (--pv-pad, styles/profile.css). Deckender Hintergrund wie der
     Kopf und die Reiterleiste: Nichts scheint durch. */
  padding: 0 var(--pv-pad, 16px);
  background: var(--bg);
}
/* Die Pfeilzeile ist höchstens 36 px hoch (Kreise 32 px) */
.pn-mid {
  min-height: 34px;
}
/* Dünne Fortschrittslinie (2 px), 4 px unter der Pfeilzeile, über die volle Breite */
.pn-progress {
  flex: 0 0 100%;
  height: 2px;
  margin-top: 4px;
  border-radius: 2px;
  background: var(--border-soft);
  overflow: hidden;
}
.pn-progress-fill {
  display: block;
  height: 100%;
  background: var(--text);
  opacity: 0.7;
  transition: width 0.2s;
}
/* Sichtbar 32 px; die Klickfläche ist per ::after unsichtbar auf 44 px erweitert. */
.pn-btn {
  position: relative;
  flex: none;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--border-soft);
  border-radius: 50%;
  color: var(--muted);
  cursor: pointer;
  transition: color 0.14s, border-color 0.14s;
}
.pn-btn:hover:not(:disabled) {
  color: var(--text);
  border-color: var(--border);
}
.pn-btn:focus-visible {
  outline: 2px solid var(--green);
  outline-offset: -2px;
}
.pn-btn:disabled {
  opacity: 0.35;
  cursor: default;
}
.pn-mid {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 1.3;
}
.pn-btn::after {
  content: '';
  position: absolute;
  inset: -7px;
}
.pn-count {
  font-size: var(--pv-meta, 13px);
  font-weight: 400;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}
.pn-note {
  font-size: var(--pv-label, 11px);
  font-weight: 500;
  color: var(--amber);
}
</style>
